"use client"

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"

const LOCAL_STORAGE_KEY = "commerce.cart"

export interface CartItem {
    priceEntryId: string
    quantity: number
    title: string
    productSlug?: string
    variantName?: string
    currency: string
    amountMinor: number
    isFree: boolean
    stripePriceId?: string | null
}

export interface CartSummaryItem extends CartItem {
    lineTotalMinor: number
}

export interface CartSummary {
    items: CartSummaryItem[]
    totals: Array<{ currency: string; amountMinor: number }>
}

export interface CartItemInput extends Omit<CartItem, "quantity"> {
    quantity?: number
}

interface CartContextValue {
    cart: CartSummary
    loading: boolean
    addItem: (item: CartItemInput) => Promise<void>
    updateQuantity: (priceEntryId: string, quantity: number) => Promise<void>
    removeItem: (priceEntryId: string) => Promise<void>
    clear: () => Promise<void>
    refresh: () => Promise<void>
}

const CartContext = createContext<CartContextValue | undefined>(undefined)

function clampQuantity(value: number): number {
    if (!Number.isFinite(value)) return 1
    return Math.max(1, Math.min(99, Math.floor(value)))
}

function sanitizeItem(raw: Partial<CartItem>): CartItem | null {
    if (!raw?.priceEntryId || typeof raw.priceEntryId !== "string") {
        return null
    }

    const quantity = clampQuantity(Number(raw.quantity ?? 1))
    const amountMinor = Number(raw.amountMinor ?? 0)
    if (!Number.isFinite(amountMinor)) {
        return null
    }

    return {
        priceEntryId: raw.priceEntryId,
        quantity,
        title: typeof raw.title === "string" ? raw.title : "",
        productSlug: typeof raw.productSlug === "string" ? raw.productSlug : undefined,
        variantName: typeof raw.variantName === "string" ? raw.variantName : undefined,
        currency: typeof raw.currency === "string" ? raw.currency.toUpperCase() : "USD",
        amountMinor,
        isFree: Boolean(raw.isFree) || amountMinor === 0,
        stripePriceId: typeof raw.stripePriceId === "string" ? raw.stripePriceId : null,
    }
}

function buildSummary(items: CartItem[]): CartSummary {
    const totals = new Map<string, number>()
    const summaryItems: CartSummaryItem[] = items.map((item) => {
        const clampedQuantity = clampQuantity(item.quantity)
        const lineTotal = item.amountMinor * clampedQuantity
        const currency = item.currency.toUpperCase()

        const previous = totals.get(currency) ?? 0
        totals.set(currency, previous + lineTotal)

        return {
            ...item,
            quantity: clampedQuantity,
            lineTotalMinor: lineTotal,
            currency,
        }
    })

    return {
        items: summaryItems,
        totals: Array.from(totals.entries()).map(([currency, amountMinor]) => ({ currency, amountMinor })),
    }
}

function readItemsFromStorage(): CartItem[] {
    if (typeof window === "undefined") {
        return []
    }

    try {
        const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY)
        if (!stored) return []

        const parsed = JSON.parse(stored)
        if (!Array.isArray(parsed)) return []

        const sanitized = parsed
            .map((entry) => sanitizeItem(entry))
            .filter((entry): entry is CartItem => Boolean(entry))

        return sanitized
    } catch (error) {
        console.error("Failed to read cart from storage", error)
        return []
    }
}

function persistItems(items: CartItem[]) {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items))
    } catch (error) {
        console.error("Failed to persist cart", error)
    }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState<CartItem[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const initial = readItemsFromStorage()
        setItems(initial)
        setLoading(false)
    }, [])

    const cart = useMemo(() => buildSummary(items), [items])

    const persistAndSet = useCallback((updater: (previous: CartItem[]) => CartItem[]) => {
        setItems((previous) => {
            const next = updater(previous)
            persistItems(next)
            return next
        })
    }, [])

    const addItem = useCallback(
        async (input: CartItemInput) => {
            const sanitized = sanitizeItem({ ...input })
            if (!sanitized) {
                toast.error("Unable to add item to cart.")
                return
            }

            persistAndSet((previous) => {
                const existingIndex = previous.findIndex((item) => item.priceEntryId === sanitized.priceEntryId)
                if (existingIndex >= 0) {
                    const next = [...previous]
                    const existing = next[existingIndex]
                    next[existingIndex] = {
                        ...existing,
                        quantity: clampQuantity(existing.quantity + clampQuantity(input.quantity ?? 1)),
                    }
                    return next
                }

                return [
                    ...previous,
                    {
                        ...sanitized,
                        quantity: clampQuantity(input.quantity ?? 1),
                    },
                ]
            })
        },
        [persistAndSet]
    )

    const updateQuantity = useCallback(
        async (priceEntryId: string, quantity: number) => {
            persistAndSet((previous) =>
                previous.map((item) =>
                    item.priceEntryId === priceEntryId ? { ...item, quantity: clampQuantity(quantity) } : item
                )
            )
        },
        [persistAndSet]
    )

    const removeItem = useCallback(
        async (priceEntryId: string) => {
            persistAndSet((previous) => previous.filter((item) => item.priceEntryId !== priceEntryId))
        },
        [persistAndSet]
    )

    const clear = useCallback(async () => {
        persistAndSet(() => [])
    }, [persistAndSet])

    const refresh = useCallback(async () => {
        setLoading(true)
        const restored = readItemsFromStorage()
        setItems(restored)
        setLoading(false)
    }, [])

    const value = useMemo<CartContextValue>(
        () => ({ cart, loading, addItem, updateQuantity, removeItem, clear, refresh }),
        [addItem, cart, clear, loading, refresh, removeItem, updateQuantity]
    )

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
    const context = useContext(CartContext)
    if (!context) {
        throw new Error("useCart must be used within CartProvider")
    }
    return context
}
